/* eslint-disable react/prop-types */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { omit } from 'lodash';
import { connect } from 'react-redux';

import ModalOpener from './modal-opener/ModalOpener';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

export function withModal(WrappedComponent, hocProps) {
  const shouldListenToEsc = 'shouldListenToEsc' in hocProps && hocProps.shouldListenToEsc === true;
  let isClosingStarted = false;

  class ModalHOC extends React.Component {
    static displayName = `ModalHOC(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    timeoutId = null;

    modalContainer = document.createElement('div');

    state = {
      modal: null,
      autoOpenModal: this.props.autoOpenModal || hocProps.autoOpenModal
    };

    componentWillUnmount() {
      if (document.body && document.body.contains(this.modalContainer)) {
        document.body.removeChild(this.modalContainer);
      }

      clearTimeout(this.timeoutId);
    }

    handleClose = (event) => {
      this.setState({
        modal: null,
        autoOpenModal: false
      });

      if (document.body && document.body.contains(this.modalContainer)) {
        document.body.removeChild(this.modalContainer);
      }

      if (shouldListenToEsc) {
        window.removeEventListener('keydown', this.handleKeyPress);
      }

      isClosingStarted = false;

      if (hocProps && hocProps.onModalClosed) {
        hocProps.onModalClosed(event);
      }
    };

    handleBeforeClose = () => {
      const { promptdisabled, dispatch } = this.props;
      const prompt = document.querySelector('.js-prompt-exit-modal');

      isClosingStarted = true;

      if (prompt && prompt.dataset.message && !promptdisabled) {
        // ask the user
        dispatch(showConfirmModal({
          title: decodeURIComponent(prompt.dataset.title),
          message: decodeURIComponent(prompt.dataset.message),
          confirmButtonLabel: decodeURIComponent(prompt.dataset.confirmButtonLabel ?? ''),
          cancelButtonLabel: decodeURIComponent(prompt.dataset.cancelButtonLabel ?? ''),
          confirmButtonColor: decodeURIComponent(prompt.dataset.actionButtonColor ?? ''),
          onConfirm: () => { // close the modal
            dispatch(hideConfirmModal());
            this.handleClose();
          },
          // keep showing the modal
          onCancel: () => {
            dispatch(hideConfirmModal());
            isClosingStarted = false;
          }
        }));
      } else {
        // close the modal (default behavior)
        this.handleClose();
      }
    };

    handleKeyPress = (event) => {
      if (isClosingStarted || event.key !== 'Escape') {
        return;
      }

      if (typeof event.target.closest !== 'function') { // some IE does not support it
        return;
      }

      if (!event.target.closest('.form-field')) { // if it was not triggered from some form elements, then close
        event.preventDefault();
        event.stopPropagation();
        this.handleBeforeClose();
      }
    };

    handleOpen = (event) => {
      const { modalContent: ModalContent, ...modalProps } = hocProps;
      const { modalContentProps } = this.props;

      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      // Only render and mount modal content on the onClick event
      this.setState({
        autoOpenModal: false,
        modal: ReactDOM.createPortal(
          <ModalOpener
            key={`${Math.random()}`}
            onClose={this.handleBeforeClose}
            {...modalProps}
            open
          >
            <ModalContent
              {...modalContentProps}
              onBeforeCloseModal={this.handleBeforeClose}
              onCloseModal={this.handleClose}
            />
          </ModalOpener>,
          this.modalContainer
        )
      });

      if (document.body) {
        document.body.appendChild(this.modalContainer);
      }

      if (shouldListenToEsc) {
        window.addEventListener('keydown', this.handleKeyPress);
      }
    };

    render() {
      let wrappedComponent;

      const wrappedComponentProps = omit(this.props, ['modalContentProps', 'dispatch']);

      const wrappedComponentPropsObj = {
        key: 'modal__wrapped-component',
        ...wrappedComponentProps
      };

      // IMPORTANT: Keep in mind that the HOC passes `onClick` as a prop to the component that it's wrapping.
      // This means that you *must* use the passed `onClick` prop on your component's outermost element for it to
      // trigger the modal. e.g. <div class="my-component" onClick={onClick}>
      if (!hocProps.byRef) {
        wrappedComponentPropsObj.onClick = this.handleOpen;
      }

      const { autoOpenModal } = this.state;

      if (autoOpenModal) {
        this.timeoutId = setTimeout(this.handleOpen, 0); // wrap into setTimeout to avoid setting state directly in render
      }

      if (typeof WrappedComponent === 'function') {
        wrappedComponent = (
          <WrappedComponent
            {...wrappedComponentPropsObj}
          />
        );
      } else {
        wrappedComponent = React.cloneElement(WrappedComponent, {
          ...wrappedComponentPropsObj
        });
      }

      return [
        wrappedComponent,
        this.state.modal
      ];
    }
  }

  return connect()(ModalHOC);
}
